import {Component, inject, Input, OnInit} from '@angular/core';
import { ApiHomeRow, ApiLiveSchedule, ApiLiveScheduleList, ApiLiveStream } from '@tytapp/api';
import { HomeCardComponentType } from '@tytapp/app/pages/home-page/home-cards.service';
import { BillingService } from '@tytapp/billing';
import { Icon, Link, PageComponent, PeriodicTaskService, TaskHandle } from '@tytapp/common';
import { isServerSide } from '@tytapp/environment-utils';
import { LiveStreamsService } from '@tytapp/live-streams';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'tyt-live-schedule',
    templateUrl: './live-schedule.component.html',
    styleUrls: ['./live-schedule.component.scss'],
    standalone: false
})
export class LiveScheduleComponent extends PageComponent implements HomeCardComponentType, OnInit {
    private liveStreamsService = inject(LiveStreamsService);
    private billing = inject(BillingService);
    private periodicTasks = inject(PeriodicTaskService);


    // Home Card

    private scheduleSubscription: Subscription;
    card: ApiHomeRow;
    title = 'Live Schedule';
    icon: Icon = { type: 'material', text: 'podcasts' };
    more: Link = { url: '/live/schedule', label: 'Full Schedule', icon: { type: 'material', text: 'open_in_browser' } };

    // State

    state: 'loading' | 'ready' = 'loading';
    useTimezone: 'local' | 'et' = 'local';
    streams: ApiLiveStream[];
    timezoneLabel = null;
    timezone: string = null;
    entitled = false;
    schedule: ApiLiveScheduleList[];
    upcomingEvents: ApiLiveSchedule[];

    private _numberOfDays: number = 2; // Default to 2 day

    @Input() get numberOfDays() { return this._numberOfDays; }
    set numberOfDays(value) {
        this._numberOfDays = value;
        setTimeout(() => this.determineEvents());
    }

    _maximumEvents = 8;

    @Input()
    get maximumEvents() {
        return this._maximumEvents;
    }

    set maximumEvents(value) {
        this._maximumEvents = value;
        setTimeout(() => this.determineEvents());
    }

    @Input() mode: 'events' | 'days' | 'full' = 'days';
    @Input() isFullLiveSchedule = false; // Used for the full live schedule page
    @Input() displayMode: 'compact' | 'mid-size' | 'full' = 'full'; // Display Mode

    private _order: 'normal' | 'upcoming' = 'upcoming';
    @Input() get order() { return this._order; }
    set order(value) {
        this._order = value;
        setTimeout(() => this.determineEvents());
    }
    @Input() showBadge = true;
    @Input() showChip = true;
    @Input() showTime = true;
    @Input() showSubtitle = true;
    @Input() showDescription = true;
    private refreshHandle: TaskHandle;

    // Logic

    async init() {
        this.subscribe(this.billing.entitlementChanged, entitled => this.entitled = entitled);

        this.refreshHandle = this.periodicTasks.schedule(1000 * 60 * 2.5, async () => {
            this.schedule = await this.liveStreamsService.getSchedule();
            this.determineEvents();
        });

        this.schedule = await this.liveStreamsService.getSchedule();
        this.determineEvents();
        this.state = 'ready';

        this.streams = await this.liveStreamsService.active();

    }

    destroy() {
        this.periodicTasks.cancel(this.refreshHandle);
    }

    async fetchLiveSchedule() {
        let customOffset = undefined;
        if (isServerSide())
            customOffset = this.getTimezoneOffset(new Date(), 'America/New_York');

        this.schedule = await this.liveStreamsService.getSchedule(customOffset);
        if (!this.schedule) {
            throw new Error(`Failed to load schedule from getSchedule() API!`);
        }

        this.determineEvents();
        this.setupTimezone();
    }

    setupTimezone() {
        let options: Intl.DateTimeFormatOptions = { timeZoneName: 'short' };
        if (isServerSide() || this.useTimezone === 'et') {
            options['timeZone'] = 'America/New_York';
        }

        let date = new Date();
        let shortTimezone = date.toLocaleTimeString('en-us', options).split(' ')[2];
        if (isServerSide() || this.useTimezone != 'local')
            this.timezone = shortTimezone;
        else
            this.timezone = null;

        this.timezoneLabel = '';
        if (this.useTimezone != 'local') {
            this.timezoneLabel = shortTimezone;
            let simpleTzMap = {
                PST: 'PT',
                PDT: 'PT',
                CST: 'CT',
                CDT: 'CT',
                MST: 'MT',
                MDT: 'MT',
                EST: 'ET',
                EDT: 'ET'
            };

            if (simpleTzMap[this.timezoneLabel]) {
                this.timezoneLabel = simpleTzMap[this.timezoneLabel];
            }
        }
    }

    isToday(event: ApiLiveSchedule) {
        return this.dayOfWeekForEvent(event) == new Date().getDay() && this.airTimeForItem(event) < Date.now() + 1000*60*60*24;
    }

    isUpcoming(event: ApiLiveSchedule) {
        return Date.now() < this.airTimeForItem(event);
    }

    dayOfWeekForEvent(event: ApiLiveSchedule) {
        let daysOfTheWeek = [ 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday' ];
        return daysOfTheWeek.indexOf((event.day || 'monday').toLowerCase())
    }

    determineEvents() {
        if (!this.schedule?.length)
            return;

        // Gather events and adjust past events into future ones
        this.upcomingEvents = this.schedule.map(x => x.live_schedules).flat();
        for (let event of this.upcomingEvents) {
            if (this.isEndedBySchedule(event) && Number(event.air_time_unix) * 1000 < Date.now())
                event.air_time_unix += 60*60*24*7;
        }

        // Handle ordering
        if (this.order === 'upcoming') {
            this.upcomingEvents = this.upcomingEvents.sort((a, b) => (this.airTimeForItem(a) - this.airTimeForItem(b)));
        }

        // Handle limit
        if (this.upcomingEvents.length > 0) {
            if (this.mode === 'events') {
                this.upcomingEvents = this.upcomingEvents.slice(0, this.maximumEvents);
            } else if (this.mode === 'days') {
                let allowedDays = [];
                let firstDay = this.dayOfWeekForEvent(this.upcomingEvents[0]);
                for (let i = 0; i < Math.min(7, this.numberOfDays); ++i) {
                    allowedDays.push((firstDay + i) % 7);
                }

                let firstDisallowedEvent = this.upcomingEvents.findIndex(x => !allowedDays.includes(this.dayOfWeekForEvent(x)));
                this.upcomingEvents = this.upcomingEvents.slice(0, firstDisallowedEvent);
            }
        }

        this.nextItem = this.upcomingEvents.find(x => !this.isLiveBySchedule(x) && Date.now() < this.airTimeForItem(x));
    }

    nextItem: ApiLiveSchedule;

    todayCount = 0;

    limitArray(array: any[], length) {
        if (!array)
            return [];

        return array.slice(0, length);
    }

    getTimezoneOffset(d, tz) {
        var a = d.toLocaleString("ja", { timeZone: tz }).split(/[\/\s:]/);
        a[1]--;
        var t1 = Date.UTC.apply(null, a);
        var t2 = new Date(d).setMilliseconds(0);
        return (t2 - t1) / 60 / 1000 / 60;
    }

    getEventStream(event: ApiLiveSchedule): ApiLiveStream {
        if (!this.streams)
            return null;

        let streams = this.streams.filter(x => x.active_live_event && x.active_live_event.id == event.id);
        let premiumStream = streams.filter(x => x.premium)[0];
        let publicStream = streams.filter(x => !x.premium)[0];

        if (this.entitled)
            return premiumStream || publicStream;

        return publicStream || premiumStream;
    }

    liveStreamRoute(event: ApiLiveSchedule) {
        const eventStream = this.getEventStream(event);
        return eventStream ? `/live/streams/${ eventStream.id }` : undefined;
    }

    airTimeForItem(item: ApiLiveSchedule) {
        return item.air_time_unix ? Number(item.air_time_unix) * 1000 : undefined;
    }

    durationInMinutes(event: ApiLiveSchedule) {
        return parseFloat(event.duration_min || event.duration || '15');
    }

    endTimeForItem(event: ApiLiveSchedule) {
        return this.airTimeForItem(event) + this.durationInMinutes(event) * 60 * 1000;
    }

    isLiveNow(event: ApiLiveSchedule): boolean {
        if (event?.is_replay) return false;

        const stream = this.getEventStream(event);
        if (!stream || !stream.active_live_event) return false;
        if (stream.active_live_event.id !== event.id) return false;
        if (stream.is_ended) return false;
        if (!this.isLiveBySchedule(event)) return false;

        return true;
    }

    isLiveBySchedule(event: ApiLiveSchedule) {
        const now = Date.now();
        const start = this.airTimeForItem(event);
        const end = this.endTimeForItem(event);
        return start <= now && now <= end;
    }

    isEndedBySchedule(event: ApiLiveSchedule) {
        const now = Date.now();
        const start = this.airTimeForItem(event);
        const end = this.endTimeForItem(event);
        return end < now;
    }

    getActiveAdhocStreams(): ApiLiveStream[] {
        return this.streams?.filter(
            stream => (!stream.live_events || stream.live_events.length === 0) && !!stream.active_live_event
        ) || [];
    }


    getCurrentLiveStream(): ApiLiveSchedule | null {
        const liveNow = this.upcomingEvents
            ?.filter(event => this.isLiveNow(event))
            .sort((a, b) => Number(a.air_time_unix) - Number(b.air_time_unix));

        return liveNow?.[0] || null;
    }

    getCurrentLiveEvent(): ApiLiveSchedule | null {
        const liveNow = this.upcomingEvents
        ?.filter(event => this.isLiveNow(event))
        .sort((a, b) => this.airTimeForItem(a) - this.airTimeForItem(b));

        return liveNow?.[0] || null;

    }


}
